import type { ItemList, ParsedBotMessage } from './types';
import type { MenuItem } from '@/types/item';

/**
 * Parse bot messages from streaming response
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function parseBotMessages(
  parts: { type: string; text?: string }[]
): ParsedBotMessage {
  const result: ParsedBotMessage = {
    answer: '',
    suggestions: [],
    items: [],
    item_descriptions: {},
  };

  const suggestionMap = new Map<string, string>();
  let combined = '';

  // Join all streamed parts
  for (const part of parts) {
    if (part.type === 'text' && part.text) {
      combined += part.text;
    }
  }

  // Split by line and look for event/data pairs
  const lines = combined.split('\n');
  let currentEventType: string | null = null;

  for (const line of lines) {
    if (line.startsWith('event: ')) {
      currentEventType = line.slice(7); // keep raw name
    } else if (line.startsWith('data: ') && currentEventType) {
      const rawData = line.slice(6); // preserve spacing

      try {
        if (currentEventType === 'answer') {
          result.answer += rawData;
        } else if (currentEventType.startsWith('suggest_')) {
          const prev = suggestionMap.get(currentEventType) ?? '';
          suggestionMap.set(currentEventType, prev + rawData);
        } else if (currentEventType.startsWith('item_')) {
          // Handle item descriptions streaming with pattern: item_12345
          const itemId = currentEventType.slice(5); // Remove 'item_' prefix
          const prev = result.item_descriptions[itemId] ?? '';
          result.item_descriptions[itemId] = prev + rawData;
        } else if (currentEventType === 'json') {
          // Handle JSON data with item descriptions
          const parsed = JSON.parse(rawData);
          if (parsed.results && Array.isArray(parsed.results)) {
            parsed.results.forEach((resultItem: any) => {
              if (resultItem.item && resultItem.reason) {
                result.item_descriptions[resultItem.item] = resultItem.reason;
              }
            });
          }
        } else if (currentEventType === 'tool_data') {
          const parsed = JSON.parse(rawData);
          result.items = parsed.item_list || [];
        }
      } catch (e) {
        console.warn('Failed to parse', { currentEventType, rawData }, e);
      }
    }
  }

  result.answer = normalizeText(result.answer);
  result.suggestions = Array.from(suggestionMap.values()).map(normalizeText);

  // Normalize item descriptions
  for (const itemId in result.item_descriptions) {
    result.item_descriptions[itemId] = normalizeText(
      result.item_descriptions[itemId]
    );
  }

  return result;
}

/**
 * Normalize text by removing extra spaces and fixing punctuation
 */
export function normalizeText(text: string): string {
  return text
    .replace(/\s+/g, ' ') // normalize multiple spaces
    .replace(/ ?([,.!?])/g, '$1') // remove space before punctuation
    .trim();
}

/**
 * Convert ItemList to MenuItem
 */
export function convertToMenuItem(item: ItemList | null): MenuItem | null {
  if (!item) return null;

  const { _id, name, price, discount_price, image_thumb_url = '' } = item;
  return {
    _id: Number(_id), // Convert string to number
    name,
    price,
    discounted_price: discount_price,
    image_thumb_url,
  };
}

/**
 * Get thread messages key for localStorage
 */
export function getThreadMessagesKey(threadId: string): string {
  return `ahafood.chat.messages.${threadId}`;
}

/**
 * Update thread session info in localStorage
 */
export function updateThreadSession(
  threadId: string,
  userId: string,
  time: number
): void {
  if (threadId && userId) {
    const key = 'ahafood.chat.thread';
    const raw = localStorage.getItem(key);
    let info: {
      threadId: string;
      userId: string;
      lastMessageTime: number;
    } | null = null;

    if (raw) {
      try {
        info = JSON.parse(raw);
      } catch {
        /* empty */
      }
    }

    if (!info || info.threadId !== threadId || info.userId !== userId) {
      localStorage.setItem(
        key,
        JSON.stringify({ threadId, userId, lastMessageTime: time })
      );
    } else {
      info.lastMessageTime = time;
      localStorage.setItem(key, JSON.stringify(info));
    }
  }
}
